import { useMemo } from 'react';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useQuery } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import IFundOperations from '../../interfaces/fund/operations/IFundOperations';
import IFundFilters from '../../interfaces/fund/IFundFilters';
import _ from 'lodash';
import AppError from '../../../../app/features/error/services/AppError';

const useInvestFundOperations = (fundId: number | null, filters: IFundFilters) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();
	const filter = {
		fundId: fundId,
		userId: auth.authOverrideUserId,
		investorIds: filters.investorIds,
	};

	const { data, isFetching } = useQuery(
		['investmentFundOperations', auth.authOverrideUserId ?? 'null', filter],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query ($fundId: Int, $investorIds: [Int]) {
							investmentFundOperations(fundId: $fundId, investorIds: $investorIds) {
								cashFlows {
									quarter
									cumulatedCashInflows
									cumulatedCashOutflows
									callableCapital
									cumulatedCashFlows
									currency
								}
								distributions {
									date
									valuePerShare
									yourDistribution
									currency
								}
								capitalCalls {
									date
									callId
									amount
									currency
								}
								shareValues {
									reportingPeriod {
										tag
										name
									}
									nominalPrice
									navPerShare
									totalNav
									nbOfShares
									currency
								}
							}
						}
					`,
					filter,
				)) as { investmentFundOperations: IFundOperations };
				return data.investmentFundOperations;
			} catch (e) {
				throw new AppError(e, 'useInvestFundOperations');
			}
		},
		{
			enabled:
				!!filter.fundId && auth.currentCompany !== null && auth.isInvestor === true && auth.isAuthenticated,
		},
	);

	// Aggregate distributions per date so the UI shows one row per date.
	// Don't mutate `data` directly — react-query keeps it in cache.
	const fundOperations = useMemo<IFundOperations | null>(() => {
		if (!data) return null;
		return {
			...data,
			distributions: _.map(_.groupBy(data.distributions, 'date'), (o) => ({
				...o[0],
				yourDistribution: _.sumBy(o, 'yourDistribution'),
			})),
		};
	}, [data]);

	return {
		isFetching,
		distributions: fundOperations ? _.orderBy(fundOperations.distributions, ['date'], ['desc']) ?? null : null,
		capitalCalls: fundOperations ? _.orderBy(fundOperations.capitalCalls, ['date'], ['desc']) : null,
		shareValues: fundOperations ? _.orderBy(fundOperations.shareValues, ['reportingPeriod.tag'], ['desc']) : null,
		cashFlows: fundOperations ? fundOperations.cashFlows : null,
	};
};

export default useInvestFundOperations;
